import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { TJob, TEmocEmoc } from '../../../lib/types';
import { GET_EMOC_BY_MOC_NUMBERS } from '../../../graph-queries/tar/emoc';
import {
  GET_PERMITS_BY_EVENT,
  GET_PERMITS_BY_PERMIT_NUMBERS,
} from '../../../graph-queries/tar/permit';
import {
  ECOW_INUSLATION_STATUS_MAPPING,
  ECOW_ISOLATION_STATUS_MAPPING,
  ECOW_PERMIT_STATUS_MAPPING,
  EMOC_STATUS_MAPPING,
} from '../../../lib/constants/dbConstants';
import {
  Asset,
  Event,
  GocStatusEnum,
  InsulationStatusEnum,
  JobFieldWalkStatusEnum,
  ScaffoldStatusEnum,
} from '../../../graph-queries/__generated__/graphql';
import { GET_JOBS_BY_EVENT } from '../../../graph-queries/tar/job';
import { useJobIsolations } from '../../pages/Jobs/Jobs.util';
import { getTargetKpi } from '../ReadinessCheck/ReadinessCheck.util';

export type KpiMetricType =
  | 'PERMITS_VERIFIED'
  | 'IDPS_AWAITING_AUTHORISATION'
  | 'PROCEDURES_APPROVED'
  | 'LINE_WALK_PACKS_APPROVED'
  | 'GOC_READY_TO_USE'
  | 'TEST_PACKS_APPROVED'
  | 'JOBS_AT_LEVEL_4_SCHEDULED'
  | 'WORKPACKS_APPROVED'
  | 'LIFT_PLANS_APPROVED'
  | 'SCAFFOLDS_SURVEYED_AND_PLANNED'
  | 'INSULATION_SURVEYED_AND_PLANNED'
  | 'ISOLATIONS_WAITING_FOR_AUTHORIZATION'
  | 'MOCS_APPROVED'
  | 'JOBS_FIELD_WALKED'
  | 'JOBS_AT_LEVEL_3_SCHEDULED';

export type KpiCardData = {
  metricType: KpiMetricType;
  title: string;
  subtitle: string;
  completionCount: number;
  totalCount: number;
  loading?: boolean;
  error?: unknown;
  target?: number;
};

export const useKpiMetrics = (asset?: Asset, event?: Event) => {
  const { data, loading } = useQuery(GET_JOBS_BY_EVENT, {
    variables: { eventId: event?.eventId || -1 },
    skip: !event?.eventId,
  });
  const jobs = (data?.jobsByEventId as unknown as TJob[]) || [];
  const isolations = useJobIsolations(jobs);
  const isolationCount = useMemo(() => {
    const uniqueIds = new Set<number>();

    for (const job of jobs) {
      const isolations = job.tarIsolations ?? [];

      for (const iso of isolations) {
        if (iso?.isolationId != null) {
          uniqueIds.add(iso.isolationId);
        }
      }
    }

    return uniqueIds.size;
  }, [jobs]);

  const uniqueMOCNumbers = useMemo(
    () => [
      ...new Set(
        jobs
          .filter((job) => job.mocRequired)
          .map((job) => job.tarMoc)
          .map((moc) => moc?.mocNumber)
          .filter((mocNumber) => mocNumber !== undefined)
      ),
    ],
    [jobs]
  );

  const {
    data: mocData,
    loading: mocLoading,
    error: mocError,
  } = useQuery(GET_EMOC_BY_MOC_NUMBERS, {
    variables: {
      mocNumbers: uniqueMOCNumbers.join(',') as unknown as string,
      assetId: asset?.assetId || -1,
    },
    skip: !(uniqueMOCNumbers && uniqueMOCNumbers.length > 0 && asset?.assetId),
  });

  const mocs = mocData?.emocsByEmocNumbers as unknown as TEmocEmoc[];

  const {
    data: permitsData,
    loading: permitsLoading,
    error: permitsError,
  } = useQuery(GET_PERMITS_BY_EVENT, {
    variables: { eventId: event?.eventId || -1 },
    skip: !event?.eventId,
  });

  const permitNumbers =
    permitsData?.permitsByEventId
      .filter(
        (permit) =>
          permit?.permitRequirement === 'REQUIRED' &&
          permit?.permitNumber !== '' &&
          permit?.lastKnownTaskDescription
      )
      .map((permit) => permit?.permitNumber) || [];

  const {
    data: ecowPermitsData,
    loading: ecowPermitsLoading,
    error: ecowPermitsError,
  } = useQuery(GET_PERMITS_BY_PERMIT_NUMBERS, {
    variables: { permitNumbers: (permitNumbers as unknown as string[]) || [] },
  });

  const { target: scheduleTarget } = event
    ? getTargetKpi(event, 'JOBS_AT_LEVEL_4_SCHEDULED')
    : { target: undefined };
  const { target: workpackTarget } = event
    ? getTargetKpi(event, 'WORKPACKS_APPROVED')
    : { target: undefined };
  const { target: liftPlanTarget } = event
    ? getTargetKpi(event, 'LIFT_PLANS_APPROVED')
    : { target: undefined };
  const { target: proceduresTarget } = event
    ? getTargetKpi(event, 'PROCEDURES_APPROVED')
    : { target: undefined };
  const { target: scaffoldTarget } = event
    ? getTargetKpi(event, 'SCAFFOLDS_SURVEYED_AND_PLANNED')
    : { target: undefined };
  const { target: insulationTarget } = event
    ? getTargetKpi(event, 'INSULATION_SURVEYED_AND_PLANNED')
    : { target: undefined };
  const { target: testPackTarget } = event
    ? getTargetKpi(event, 'TEST_PACKS_APPROVED')
    : { target: undefined };
  const { target: permitTarget } = event
    ? getTargetKpi(event, 'PERMITS_VERIFIED')
    : { target: undefined };
  const { target: isoaltionTarget } = event
    ? getTargetKpi(event, 'IDPS_AWAITING_AUTHORISATION')
    : { target: undefined };
  const { target: lineWalkTarget } = event
    ? getTargetKpi(event, 'LINE_WALK_PACKS_APPROVED')
    : { target: undefined };
  const { target: gocTarget } = event
    ? getTargetKpi(event, 'GOC_READY_TO_USE')
    : { target: undefined };

  const kpiMetrics: { [key in KpiMetricType]?: KpiCardData } = {
    JOBS_AT_LEVEL_3_SCHEDULED: {
      metricType: 'JOBS_AT_LEVEL_3_SCHEDULED',
      title: 'Level 3 scheduled',
      subtitle: 'Jobs at level 3 scheduled',
      completionCount: useMemo(
        () =>
          jobs.filter((job) => job.scheduleStatus === 'L3' || job.scheduleStatus === 'L4').length,
        [jobs]
      ),
      totalCount: jobs.length,
    },
    JOBS_AT_LEVEL_4_SCHEDULED: {
      metricType: 'JOBS_AT_LEVEL_4_SCHEDULED',
      title: 'Level 4 scheduled',
      subtitle: 'Jobs at level 4 scheduled',
      completionCount: useMemo(
        () => jobs.filter((job) => job.scheduleStatus === 'L4').length,
        [jobs]
      ),
      totalCount: jobs.length,
      target: scheduleTarget,
    },
    JOBS_FIELD_WALKED: {
      metricType: 'JOBS_FIELD_WALKED',
      title: 'Jobs field walked',
      subtitle: 'Jobs that have been walked',
      completionCount: useMemo(
        () =>
          jobs.filter(
            (job) =>
              job.fieldWalk === JobFieldWalkStatusEnum.VirtualComplete ||
              job.fieldWalk === JobFieldWalkStatusEnum.FieldComplete
          ).length,
        [jobs]
      ),
      totalCount: useMemo(
        () =>
          jobs.filter(
            (job) =>
              job.fieldWalk === JobFieldWalkStatusEnum.Required ||
              job.fieldWalk === JobFieldWalkStatusEnum.VirtualComplete ||
              job.fieldWalk === JobFieldWalkStatusEnum.FieldComplete
          ).length,
        [jobs]
      ),
    },
    WORKPACKS_APPROVED: {
      metricType: 'WORKPACKS_APPROVED',
      title: 'Workpacks',
      subtitle: 'Workpacks approved',
      completionCount: useMemo(
        () =>
          new Set(
            jobs
              .map((job) => (job.workpackRequired ? job.tarWorkpacks : []))
              .flat()
              .filter((workpack) => workpack?.status === 'APPROVED_FOR_USE')
              .map((workpack) => workpack?.workpackId)
          ).size,
        [jobs]
      ),
      totalCount: useMemo(
        () =>
          new Set(
            jobs
              .map((job) => (job.workpackRequired ? job.tarWorkpacks : []))
              .flat()
              .map((workpack) => workpack?.workpackId)
          ).size,
        [jobs]
      ),
      target: workpackTarget,
    },
    LIFT_PLANS_APPROVED: {
      metricType: 'LIFT_PLANS_APPROVED',
      title: 'Lift Plans',
      subtitle: 'Lift plans approved',
      completionCount: useMemo(
        () =>
          new Set(
            jobs
              .map((job) => (job.liftPlanRequired ? job.tarLiftPlans : []))
              .flat()
              .filter((liftPlan) => liftPlan?.status === 'APPROVED_FOR_USE')
              .map((liftPlan) => liftPlan?.liftPlanId)
          ).size,
        [jobs]
      ),
      totalCount: useMemo(
        () =>
          new Set(
            jobs
              .map((job) => (job.liftPlanRequired ? job.tarLiftPlans : []))
              .flat()
              .map((liftPlan) => liftPlan?.liftPlanId)
          ).size,
        [jobs]
      ),
      target: liftPlanTarget,
    },
    PROCEDURES_APPROVED: {
      metricType: 'PROCEDURES_APPROVED',
      title: 'Procedures',
      subtitle: 'Procedures approved',
      completionCount: useMemo(
        () =>
          new Set(
            jobs
              .map((job) => job.tarProcedures)
              .flat()
              .filter((aspect) => aspect?.status === 'APPROVED_FOR_USE')
              .map((aspect) => aspect?.procedureId)
          ).size,
        [jobs]
      ),
      totalCount: useMemo(
        () =>
          new Set(
            jobs
              .map((job) => job.tarProcedures)
              .flat()
              .map((aspect) => aspect?.procedureId)
          ).size,
        [jobs]
      ),
      target: proceduresTarget,
    },
    SCAFFOLDS_SURVEYED_AND_PLANNED: {
      metricType: 'SCAFFOLDS_SURVEYED_AND_PLANNED',
      title: 'Scaffolds',
      subtitle: 'Scaffolds surveyed and planned',
      completionCount: useMemo(
        () =>
          new Set(
            jobs
              .map((job) => (job.scaffoldRequired ? job.tarScaffolds : []))
              .flat()
              .filter(
                (scaffold) =>
                  scaffold?.status === ScaffoldStatusEnum.SurveyComplete ||
                  scaffold?.status === ScaffoldStatusEnum.Erected
              )
              .map((scaffold) => scaffold?.scaffoldId)
          ).size,
        [jobs]
      ),
      totalCount: useMemo(
        () =>
          new Set(
            jobs
              .map((job) => (job.scaffoldRequired ? job.tarScaffolds : []))
              .flat()
              .map((scaffold) => scaffold?.scaffoldId)
          ).size,
        [jobs]
      ),
      target: scaffoldTarget,
    },
    INSULATION_SURVEYED_AND_PLANNED: {
      metricType: 'INSULATION_SURVEYED_AND_PLANNED',
      title: 'Insulations',
      subtitle: 'Insulations surveyed and planned',
      completionCount: useMemo(
        () =>
          new Set(
            jobs
              .map((job) => (job.insulationRequired ? job.tarInsulations : []))
              .flat()
              .filter(
                (aspect) =>
                  ECOW_INUSLATION_STATUS_MAPPING[
                    aspect?.status?.toLowerCase() as keyof typeof ECOW_INUSLATION_STATUS_MAPPING
                  ]?.priority >=
                  ECOW_INUSLATION_STATUS_MAPPING[
                    InsulationStatusEnum.Surveyed.toLowerCase() as keyof typeof ECOW_INUSLATION_STATUS_MAPPING
                  ]?.priority
              )
              .map((aspect) => aspect?.insulationId)
          ).size,
        [jobs]
      ),
      totalCount: useMemo(
        () =>
          new Set(
            jobs
              .map((job) => (job.insulationRequired ? job.tarInsulations : []))
              .flat()
              .map((aspect) => aspect?.insulationId)
          ).size,
        [jobs]
      ),
      target: insulationTarget,
    },
    MOCS_APPROVED: {
      metricType: 'MOCS_APPROVED',
      title: 'MOC',
      subtitle: "MOC's approved",
      completionCount: mocs
        ? mocs.filter(
            (moc) =>
              EMOC_STATUS_MAPPING[moc?.mocStatus as keyof typeof EMOC_STATUS_MAPPING]?.priority >=
              EMOC_STATUS_MAPPING['APPROVED' as keyof typeof EMOC_STATUS_MAPPING].priority
          ).length
        : 0,
      totalCount: uniqueMOCNumbers ? uniqueMOCNumbers.length : 0,
      loading: mocLoading,
      error: mocError,
    },
    ISOLATIONS_WAITING_FOR_AUTHORIZATION: {
      metricType: 'ISOLATIONS_WAITING_FOR_AUTHORIZATION',
      title: 'Isolations',
      subtitle: 'Waiting for authorization',
      completionCount: isolations
        ? isolations.filter(
            (isolation) =>
              ECOW_ISOLATION_STATUS_MAPPING[
                isolation?.statusComment as keyof typeof ECOW_ISOLATION_STATUS_MAPPING
              ]?.priority >=
              ECOW_ISOLATION_STATUS_MAPPING[
                'WaitingForAuthorization' as keyof typeof ECOW_ISOLATION_STATUS_MAPPING
              ]?.priority
          ).length
        : 0,
      totalCount: isolationCount,
      target: isoaltionTarget,
    },
    TEST_PACKS_APPROVED: {
      metricType: 'TEST_PACKS_APPROVED',
      title: 'Test Packs',
      subtitle: 'Test packs approved',
      completionCount: useMemo(
        () =>
          new Set(
            jobs
              .map((job) => job.tarLeakTestPacks)
              .flat()
              .filter((testPack) => testPack?.status === 'APPROVED_FOR_USE')
              .map((testPack) => testPack?.leakTestPackId)
          ).size,
        [jobs]
      ),
      totalCount: useMemo(
        () =>
          new Set(
            jobs
              .map((job) => job.tarLeakTestPacks)
              .flat()
              .map((testPack) => testPack?.leakTestPackId)
          ).size,
        [jobs]
      ),
      target: testPackTarget,
    },
    GOC_READY_TO_USE: {
      metricType: 'GOC_READY_TO_USE',
      title: 'GOC',
      subtitle: 'GOC ready for use',
      completionCount: useMemo(
        () => jobs.filter((job) => job.gocStatus === GocStatusEnum.Ready).length,
        [jobs]
      ),
      totalCount: useMemo(
        () => jobs.filter((job) => job.gocStatus !== GocStatusEnum.NotRequired).length,
        [jobs]
      ),
      target: gocTarget,
    },
    LINE_WALK_PACKS_APPROVED: {
      metricType: 'LINE_WALK_PACKS_APPROVED',
      title: 'Line Walk Pack',
      subtitle: 'Walk packs ready for use',
      completionCount: useMemo(
        () =>
          new Set(
            jobs
              .filter((job) => job.isolationRequired)
              .flatMap((job) => job.tarIsolations || [])
              .filter((isolation) => isolation?.lineWalkPackStatus === 'APPROVED_FOR_USE')
              .map((isolation) => isolation?.isolationId)
          ).size,
        [jobs]
      ),
      totalCount: useMemo(
        () =>
          new Set(
            jobs
              .filter((job) => job.isolationRequired)
              .flatMap((job) => job.tarIsolations || [])
              .filter((isolation) => isolation?.lineWalkPackStatus !== 'NOT_REQUIRED')
              .map((isolation) => isolation.isolationId)
          ).size,
        [jobs]
      ),
      target: lineWalkTarget,
    },
    PERMITS_VERIFIED: {
      metricType: 'PERMITS_VERIFIED',
      title: 'Permits',
      subtitle: 'Permits verified',
      completionCount: useMemo(
        () =>
          ecowPermitsData?.permitsByPermitNumbers.filter(
            (permit) =>
              ECOW_PERMIT_STATUS_MAPPING[
                permit.statusComment as keyof typeof ECOW_PERMIT_STATUS_MAPPING
              ]?.priority >=
              ECOW_PERMIT_STATUS_MAPPING['Verified' as keyof typeof ECOW_PERMIT_STATUS_MAPPING]
                ?.priority
          ).length || 0,
        [ecowPermitsData]
      ),
      totalCount: useMemo(
        // (permits that are required and have no permit number) + (no of unique permit numbers)
        () =>
          (permitsData?.permitsByEventId.filter(
            (permit) =>
              permit?.permitRequirement === 'REQUIRED' &&
              permit?.lastKnownTaskDescription &&
              (!permit?.permitNumber || permit?.permitNumber === '')
          ).length || 0) + (ecowPermitsData?.permitsByPermitNumbers.length || 0),
        [permitsData, ecowPermitsData]
      ),
      target: permitTarget,
      loading: permitsLoading || ecowPermitsLoading,
      error: permitsError || ecowPermitsError,
    },
  };

  return kpiMetrics;
};
